import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAggregatorBatches, importAggregatorCSV } from "@/lib/dashboardApi";
import { inr } from "@/lib/format";
import { AlertCircle, CheckCircle, UploadCloud } from "lucide-react";

export default function AggregatorImportModal({ open, onClose, token, onImported }) {
  const [platform, setPlatform] = useState("zomato");
  const [payoutRef, setPayoutRef] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [batches, setBatches] = useState([]);

  const loadBatches = async () => {
    try {
      const list = await getAggregatorBatches(token);
      setBatches(list || []);
    } catch (err) {
      console.error("Failed to load aggregator batches:", err);
    }
  };

  useEffect(() => {
    if (open) {
      loadBatches();
      setError(null);
      setSuccessMsg(null);
    }
  }, [open]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please select a settlement CSV file");
      return;
    }
    setError(null);
    setSuccessMsg(null);
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("platform", platform);
      formData.append("file", file);
      if (payoutRef) formData.append("payout_reference", payoutRef);
      if (periodStart) formData.append("period_start", periodStart);
      if (periodEnd) formData.append("period_end", periodEnd);

      const res = await importAggregatorCSV(formData, token);
      setSuccessMsg(
        `Imported batch #${res.batch_id}: ${res.lines_parsed} lines parsed, ${res.lines_matched} matched, ${res.lines_unmatched} unmatched.`
      );
      setFile(null);
      await loadBatches();
      if (onImported) onImported();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to import aggregator settlement");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Delivery Aggregator Settlement Import & Reconciliation</DialogTitle>
          <DialogDescription>
            Import portal settlement reports (Zomato / Swiggy) to reconcile actual bank payouts against POS invoiced orders.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleUpload} className="space-y-base rounded-md border p-base bg-muted/20">
          <div className="font-semibold text-meta">Import Settlement CSV</div>

          {error && (
            <div className="flex items-center gap-tight text-meta text-danger">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {successMsg && (
            <div className="flex items-center gap-tight text-meta text-success">
              <CheckCircle className="h-4 w-4 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-base">
            <div>
              <Label className="text-[11px]">Platform</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zomato">Zomato</SelectItem>
                  <SelectItem value="swiggy">Swiggy</SelectItem>
                  <SelectItem value="magicpin">Magicpin</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[11px]">Payout Reference / UTR</Label>
              <Input
                className="h-9"
                placeholder="e.g. ZOM-PAYOUT-9021"
                value={payoutRef}
                onChange={(e) => setPayoutRef(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-base">
            <div>
              <Label className="text-[11px]">Period Start (Optional)</Label>
              <Input
                type="date"
                className="h-9"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-[11px]">Period End (Optional)</Label>
              <Input
                type="date"
                className="h-9"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label className="text-[11px]">Select Settlement CSV</Label>
            <Input
              type="file"
              accept=".csv"
              className="h-9 cursor-pointer"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <div className="flex justify-end">
            <Button size="sm" type="submit" disabled={uploading || !file}>
              <UploadCloud className="h-4 w-4 mr-tight" />
              <span>{uploading ? "Importing…" : "Upload & Reconcile"}</span>
            </Button>
          </div>
        </form>

        {/* Recent Batches */}
        <div className="space-y-snug">
          <div className="font-semibold text-meta">Settlement Import Batches</div>
          <div className="rounded-md border divide-y text-meta max-h-48 overflow-y-auto">
            {batches.map((b) => (
              <div key={b.id} className="flex items-center justify-between p-snug">
                <div>
                  <div className="flex items-center gap-tight">
                    <span className="font-bold text-foreground capitalize">{b.platform}</span>
                    <span className="text-muted-foreground text-[11px]">#{b.id}</span>
                    {b.payout_reference && (
                      <span className="rounded bg-muted px-tight py-hair text-[10px] text-muted-foreground">
                        {b.payout_reference}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {b.lines_matched} matched / {b.total_lines} lines ({b.lines_unmatched} unmatched)
                  </div>
                </div>

                <div className="text-right tabular-nums">
                  <div className="font-bold text-foreground">{inr(b.net_payout_amount)}</div>
                  {b.total_discrepancy_amount !== 0 && (
                    <div className="text-[11px] text-danger font-medium">
                      Δ {inr(b.total_discrepancy_amount)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {batches.length === 0 && (
              <div className="p-base text-center text-muted-foreground">No settlement batches imported yet.</div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
